import { useState } from 'react';
import { Button } from './ui/button';
import { Camera, X, GripVertical } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

/**
 * Multi-photo upload component with drag-and-drop upload AND drag-reorder.
 * Order matters: photos land on the certificate in the same sequence as
 * shown here (slot 1 top-left → slot 4 bottom-right).
 */
const MultiPhotoUpload = ({ 
  photos = [],
  onChange,
  label,
  helperText,
  API,
  getAuthHeader
}) => {
  const [uploading, setUploading] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);

  const handleFileSelect = async (files) => {
    if (!files || files.length === 0) return;

    setUploading(true);
    try {
      const uploadPromises = Array.from(files).map(async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('entity_type', 'general');
        formData.append('document_type', 'photo');

        const response = await axios.post(`${API}/upload`, formData, {
          headers: {
            ...getAuthHeader(),
            'Content-Type': 'multipart/form-data'
          }
        });

        return response.data.local_url 
          ? `${API}/uploads${response.data.local_url.replace('/uploads', '')}` 
          : response.data.url;
      });

      const urls = await Promise.all(uploadPromises);
      onChange([...photos, ...urls]);
      toast.success(`${urls.length} photo(s) uploaded successfully`);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload photos');
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = (index) => {
    const newPhotos = photos.filter((_, i) => i !== index);
    onChange(newPhotos);
  };

  const reorderPhotos = (from, to) => {
    if (from === to || from == null || to == null) return;
    const next = [...photos];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onChange(next);
  };

  return (
    <div className="space-y-2">
      {label && <label className="text-sm font-medium">{label}</label>}
      {helperText && <p className="text-xs text-slate-500">{helperText}</p>}
      
      {/* Display existing photos with drag-to-reorder */}
      {photos.length > 0 && (
        <div className="space-y-2">
          {photos.map((photo, index) => (
            <div
              key={index}
              draggable
              data-testid={`photo-row-${index}`}
              onDragStart={() => setDragIndex(index)}
              onDragOver={(e) => { e.preventDefault(); setOverIndex(index); }}
              onDragLeave={() => setOverIndex(null)}
              onDrop={(e) => {
                e.preventDefault();
                reorderPhotos(dragIndex, index);
                setDragIndex(null);
                setOverIndex(null);
              }}
              onDragEnd={() => { setDragIndex(null); setOverIndex(null); }}
              className={`flex items-center gap-2 p-2 rounded transition-colors ${
                overIndex === index && dragIndex !== index
                  ? 'bg-blue-100 ring-2 ring-blue-300'
                  : dragIndex === index
                  ? 'bg-slate-200 opacity-60'
                  : 'bg-slate-50 hover:bg-slate-100'
              }`}
            >
              <div
                className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-600"
                title="Drag to reorder"
                data-testid={`photo-drag-handle-${index}`}
              >
                <GripVertical className="w-4 h-4" />
              </div>
              <div className="flex items-center gap-2 flex-1">
                <img 
                  src={photo} 
                  alt={`Photo ${index + 1}`}
                  className="w-12 h-12 object-cover rounded border"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
                <span className="text-xs text-slate-500">Photo {index + 1}</span>
              </div>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => removePhoto(index)}
                className="text-red-600 hover:text-red-700"
                data-testid={`photo-remove-${index}`}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          ))}
          <p className="text-xs text-slate-400 italic">
            Tip: drag a row by the ⋮⋮ handle to change the photo order on the certificate.
          </p>
        </div>
      )}
      
      {/* Multi-file upload with drag-and-drop */}
      <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 text-center hover:border-slate-400 transition">
        <input
          type="file"
          multiple
          accept="image/*"
          className="hidden"
          id={`multi-photo-${label}`}
          onChange={(e) => handleFileSelect(e.target.files)}
          disabled={uploading}
        />
        <label htmlFor={`multi-photo-${label}`} className="cursor-pointer">
          <div className="space-y-2">
            <Camera className="w-8 h-8 mx-auto text-slate-400" />
            <div className="text-sm font-medium text-slate-600">
              {uploading ? 'Uploading...' : 'Click to upload multiple photos'}
            </div>
            <div className="text-xs text-slate-400">
              Or drag and drop images here
            </div>
          </div>
        </label>
      </div>
    </div>
  );
};

export default MultiPhotoUpload;
